import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import { BasePanel } from "components/BasePanel";
import { useProduct } from "contexts/ProductContext";
import { toast } from "utils/toast";
import { useAnalytics } from "contexts/AnalyticsContext";
import { EmptyList } from "components/empty/EmptyList";
import { useCallback, useMemo } from "react";
import { actionInProgress, actionErrored } from "utils/status";
import { useProductActions } from "hooks/useProductActions";
import { logError } from "shared/services/ErrorReporting";
import { FirestoreProductSubject } from "reedy-data/api";
import {
  qualifierTypes,
  subjectsType,
  SubjectSchemeIdentifier,
} from "integrations/firebase/collections/products";
import { LoadingPanel, ErrorPanel } from "components/panels";
import { generationErrorMessage } from "utils/errors";
import { ThemaCodeItem } from "./ThemaCodeItem";

// Filter functions for THEMA subjects
const filterCategories = (subjects: FirestoreProductSubject[] = []) => {
  return subjects.filter(
    (subject) => subject.subjectSchemeIdentifier === subjectsType,
  );
};

const filterQualifiers = (subjects: FirestoreProductSubject[] = []) => {
  return subjects.filter((subject) =>
    qualifierTypes.includes(
      subject.subjectSchemeIdentifier as SubjectSchemeIdentifier,
    ),
  );
};

export const ThemaTab = () => {
  const { t } = useTranslation(["manuscriptDetails", "general"]);
  const { product } = useProduct();
  const { gaEvent } = useAnalytics();
  const { generateProductSubjects } = useProductActions();

  // Get thema actions and check status
  const action = product?.actions?.thema?.actions?.generate;
  const isGenerating = actionInProgress(action);
  const isError = actionErrored(action);

  // Get Thema-Codes from product data
  const categories = useMemo(() => {
    return filterCategories(product?.subjects?.generated || []);
  }, [product?.subjects?.generated]);

  const qualifiers = useMemo(() => {
    return filterQualifiers(product?.subjects?.generated || []);
  }, [product?.subjects?.generated]);

  const isEmptyCategories = categories.length === 0;
  const isEmptyQualifiers = qualifiers.length === 0;
  const isEmpty = isEmptyCategories && isEmptyQualifiers;

  const handleGenerateThema = async () => {
    if (!product) return;

    try {
      await generateProductSubjects(product.id, { regenerate: false });
    } catch (e) {
      logError(e);
      toast.error(t("manuscriptDetails:thema.error.generate"));
    }
  };

  const handleCopy = useCallback(
    (code: string) => {
      navigator.clipboard.writeText(code);
      toast.success(t("manuscriptDetails:thema.toasts.copy.success", { code }));

      gaEvent({
        type: "click",
        payload: {
          analytics_id: `copy_thema_code_${code}`,
          manuscript_file_name: product?.manuscript?.filename ?? "",
          thema_code: code,
        },
      });
    },
    [gaEvent, product, t],
  );

  // If generating, show loading panel
  if (isGenerating) {
    return (
      <LoadingPanel
        title={t("manuscriptDetails:thema.title")}
        loadingText={t("manuscriptDetails:thema.generating")}
      />
    );
  }

  // If error, show error panel
  if (isError) {
    const errorMsg =
      generationErrorMessage(t, action?.statusCode) ||
      t("manuscriptDetails:thema.error.generation", {
        code: action?.statusCode,
      });

    return (
      <ErrorPanel
        title={t("manuscriptDetails:thema.title")}
        errorTitle={t("manuscriptDetails:thema.error.title")}
        errorMessage={errorMsg}
        buttons={[
          <Button
            key="retry"
            label={t("general:buttons.retry")}
            onClick={handleGenerateThema}
            icon="sparkles"
            size="small"
            analyticsId="button_retry_thema"
          />,
        ]}
      />
    );
  }

  if (isEmpty) {
    return (
      <BasePanel
        title={t("manuscriptDetails:thema.title")}
        isEmpty
        emptyComponent={
          <EmptyList
            title={t("manuscriptDetails:thema.empty.title")}
            text={t("manuscriptDetails:thema.empty.description")}
            buttons={[
              <Button
                key="generate"
                label={t("general:buttons.generate")}
                onClick={handleGenerateThema}
                icon="sparkles"
                size="small"
                analyticsId="button_generate_thema"
              />,
            ]}
          />
        }
      />
    );
  }

  return (
    <div className="flex flex-row gap-4 h-full">
      <div className="flex-1">
        <BasePanel
          title={t("manuscriptDetails:thema.subjectsTitle")}
          isEmpty={isEmptyCategories}
          emptyComponent={
            <EmptyList
              title={t("manuscriptDetails:thema.subjectsEmpty.title")}
              text={t("manuscriptDetails:thema.subjectsEmpty.description")}
            />
          }
        >
          <div>
            {categories.map((subject, index) => (
              <ThemaCodeItem
                key={subject.id}
                subject={subject}
                index={index + 1}
                onCopy={handleCopy}
              />
            ))}
          </div>
        </BasePanel>
      </div>

      <div className="flex-1">
        <BasePanel
          title={t("manuscriptDetails:thema.qualifiersTitle")}
          isEmpty={isEmptyQualifiers}
          emptyComponent={
            <EmptyList
              title={t("manuscriptDetails:thema.qualifiersEmpty.title")}
              text={t("manuscriptDetails:thema.qualifiersEmpty.description")}
            />
          }
        >
          <div>
            {qualifiers &&
              qualifiers.map((subject, index) => (
                <ThemaCodeItem
                  key={subject.id}
                  subject={subject}
                  index={index + 1}
                  onCopy={handleCopy}
                />
              ))}
          </div>
        </BasePanel>
      </div>
    </div>
  );
};
